// Cave84 — Guest Login Log (Phase 7)
// Owner-only sub-screen under Settings. Read-only audit trail of every
// Guest (role='viewer') sign-in, written by services/users.ts:logGuestLogin
// on each successful sign-in. Firestore /guest_logins is owner-read-only,
// self-create, immutable (see firebase/firestore.rules).

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { collection, query, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { GuestLoginEvent } from '@cave84/shared-types';

const QK = ['guest_logins'] as const;
const PAGE_SIZE = 200;

async function fetchGuestLogins(): Promise<GuestLoginEvent[]> {
  const q = query(
    collection(db, 'guest_logins'),
    orderBy('logged_in_at', 'desc'),
    limit(PAGE_SIZE)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<GuestLoginEvent, 'id'>) }));
}

function formatWhen(ts: Timestamp | undefined): string {
  if (!ts) return '—';
  const d = ts.toDate();
  return `${d.toLocaleDateString('en-GB')} · ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

export default function GuestLoginLogPage() {
  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: QK,
    queryFn: fetchGuestLogins,
    staleTime: 60_000,
  });

  return (
    <div className="min-h-screen bg-surface-0">
      <div className="sticky top-0 z-10 bg-surface-1 border-b border-border-medium px-4 py-3 flex items-center gap-3">
        <Link
          to="/settings"
          className="text-text-secondary hover:text-text-primary transition-colors text-sm"
          aria-label="Back to Settings"
        >
          ← Settings
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        <div className="mb-4">
          <h1 className="text-base font-semibold text-text-primary">Guest Login Log</h1>
          <p className="text-xs text-text-muted mt-0.5">
            Every sign-in by a non-owner Gmail account. Guests get read-only access
            with no purchase prices, cellar totals, or editing.
          </p>
        </div>

        <div className="bg-surface-1 border border-border-medium rounded-lg overflow-hidden">
          {isLoading ? (
            <p className="px-4 py-6 text-sm text-text-muted animate-pulse">Loading…</p>
          ) : isError ? (
            <p className="px-4 py-6 text-sm text-claret">Failed to load the guest log.</p>
          ) : events.length === 0 ? (
            <p className="px-4 py-6 text-sm text-text-muted">No guest sign-ins yet.</p>
          ) : (
            <ul>
              {events.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-subtle last:border-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary truncate">{e.display_name}</p>
                    <p className="text-xs text-text-muted mono truncate">{e.email}</p>
                  </div>
                  <span className="flex-shrink-0 text-xs text-text-secondary mono">
                    {formatWhen(e.logged_in_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {events.length === PAGE_SIZE && (
          <p className="mt-2 text-xs text-text-muted">
            Showing the {PAGE_SIZE} most recent sign-ins.
          </p>
        )}
      </div>
    </div>
  );
}
